
import { useState, useEffect } from 'react';
import { mockProjects } from '@/data/mockProjects';
import { Project } from '@/types';

// Mock localStorage key
const PROJECTS_STORAGE_KEY = 'mock_projects';

// Deep parse function to handle nested structures
const deepParseProject = (projectData: any): Project => {
  return {
    ...projectData,
    startDate: new Date(projectData.startDate),
    targetCompletionDate: new Date(projectData.targetCompletionDate),
    createdAt: new Date(projectData.createdAt),
    updatedAt: new Date(projectData.updatedAt),
    stages: (projectData.stages || []).map((stage: any) => ({
      ...stage,
      dateStarted: stage.dateStarted ? new Date(stage.dateStarted) : null,
      dateCompleted: stage.dateCompleted ? new Date(stage.dateCompleted) : null,
      activities: stage.activities || [],
      approvals: stage.approvals || [],
      informationExchanges: (stage.informationExchanges || []).map((ie: any) => ({
        ...ie,
        dateUploaded: ie.dateUploaded ? new Date(ie.dateUploaded) : null
      }))
    })),
    risks: projectData.risks || [],
    team: projectData.team || []
  };
};

// Validate project structure
const validateProject = (project: any): boolean => {
  if (!project || typeof project !== 'object') return false;
  if (!project.id || !project.name || !project.client) return false;
  if (!Array.isArray(project.stages)) return false;
  
  // Validate each stage has required properties
  for (const stage of project.stages) {
    if (typeof stage.id !== 'number' || !stage.name) return false;
  }
  
  return true;
};

export const useMockProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('useMockProjects: Starting to load projects');
    
    const timer = setTimeout(() => {
      try {
        let projectsToLoad: Project[] = [];
        
        // Try to load from localStorage
        if (typeof localStorage !== 'undefined') {
          const stored = localStorage.getItem(PROJECTS_STORAGE_KEY);
          if (stored) {
            console.log('useMockProjects: Found stored projects, parsing...');
            try {
              const parsedData = JSON.parse(stored);
              
              if (Array.isArray(parsedData)) {
                // Validate and deep parse each project
                const validProjects = parsedData
                  .filter(project => {
                    const isValid = validateProject(project);
                    if (!isValid) {
                      console.warn('useMockProjects: Invalid project structure found:', project);
                    }
                    return isValid;
                  })
                  .map(project => deepParseProject(project));
                
                if (validProjects.length > 0) {
                  projectsToLoad = validProjects;
                  console.log('useMockProjects: Successfully parsed', validProjects.length, 'valid projects');
                } else {
                  console.log('useMockProjects: No valid projects found in stored data, using mock data');
                  projectsToLoad = mockProjects;
                  // Clear corrupted data
                  localStorage.removeItem(PROJECTS_STORAGE_KEY);
                }
              } else {
                console.log('useMockProjects: Stored data is not an array, using mock data');
                projectsToLoad = mockProjects;
                localStorage.removeItem(PROJECTS_STORAGE_KEY);
              }
            } catch (parseError) {
              console.error('useMockProjects: Error parsing stored projects:', parseError);
              projectsToLoad = mockProjects;
              // Clear corrupted data
              localStorage.removeItem(PROJECTS_STORAGE_KEY);
            }
          } else {
            console.log('useMockProjects: No stored projects found, using mock data');
            projectsToLoad = mockProjects;
            // Save mock projects to localStorage for future use
            try {
              localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(mockProjects));
              console.log('useMockProjects: Saved mock projects to localStorage');
            } catch (saveError) {
              console.error('useMockProjects: Error saving mock projects:', saveError);
            }
          }
        } else {
          console.warn('useMockProjects: localStorage not available, using mock data only');
          projectsToLoad = mockProjects;
        }
        
        // Final validation
        if (!Array.isArray(projectsToLoad) || projectsToLoad.length === 0) {
          console.warn('useMockProjects: No valid projects to load, falling back to mock data');
          projectsToLoad = mockProjects;
        }
        
        console.log('useMockProjects: Final projects to load:', projectsToLoad.length);
        console.log('useMockProjects: First project stages:', projectsToLoad[0]?.stages?.length || 'undefined');
        
        setProjects(projectsToLoad);
        setIsLoading(false);
      } catch (err) {
        console.error('useMockProjects: Critical error loading projects:', err);
        setError(err as Error);
        // Even on critical error, provide fallback data
        setProjects(mockProjects);
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return { data: projects, isLoading, error };
};

export const useMockCreateProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutateAsync = async (projectData: Omit<Project, 'id'>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('useMockCreateProject: Creating new project');
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const newProject: Project = {
        ...projectData,
        id: `project-${Date.now()}`
      };

      // Update localStorage
      if (typeof localStorage !== 'undefined') {
        try {
          const stored = localStorage.getItem(PROJECTS_STORAGE_KEY);
          const currentProjects = stored ? JSON.parse(stored) : mockProjects;
          const updatedProjects = [newProject, ...currentProjects];
          localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(updatedProjects));
          console.log('useMockCreateProject: Project saved to localStorage');
        } catch (storageError) {
          console.error('useMockCreateProject: Error updating localStorage:', storageError);
        }
      }
      
      setIsLoading(false);
      
      // Trigger page refresh to show new project
      window.location.reload();
    } catch (err) {
      console.error('useMockCreateProject: Error creating project:', err);
      setError(err as Error);
      setIsLoading(false);
      throw err;
    }
  };

  return { mutate: mutateAsync, mutateAsync, isLoading, error };
};
